import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronRight,
  faChevronDown,
  faCompressAlt,
  faExpandAlt,
} from "@fortawesome/free-solid-svg-icons";
import { CampaignAsset } from "../../types/types";
import { getTypeConfig } from "../../pages/assets/explorer/typeConfig";

// ── Types ──────────────────────────────────────────────────────────────────────

interface TreeNode extends CampaignAsset {
  _children: TreeNode[];
}

interface KnowledgeTreeProps {
  assets: CampaignAsset[];
  selectedId: number | null;
  newlyRevealedId?: number | null;
  onSelect: (_id: number) => void;
  compact?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildTree(assets: CampaignAsset[]): TreeNode[] {
  const map = new Map<number, TreeNode>();
  assets.forEach((a) => map.set(a.id, { ...a, _children: [] }));
  const roots: TreeNode[] = [];
  assets.forEach((a) => {
    if (a.parentId) {
      const parent = map.get(a.parentId);
      if (parent) {
        parent._children.push(map.get(a.id)!);
        return;
      }
    }
    roots.push(map.get(a.id)!);
  });
  return roots;
}

function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
  if (!q) return nodes;
  const lq = q.toLowerCase();
  const result: TreeNode[] = [];
  for (const n of nodes) {
    const tags = Array.isArray(n.asset.tags) ? n.asset.tags : [];
    const match =
      n.asset.name.toLowerCase().includes(lq) ||
      n.asset.type.toLowerCase().includes(lq) ||
      (n.asset.category?.toLowerCase().includes(lq) ?? false) ||
      tags.some((t) => t.toLowerCase().includes(lq));
    const childMatches = filterTree(n._children, q);
    if (match || childMatches.length > 0) result.push({ ...n, _children: childMatches });
  }
  return result;
}

function treeNodesToIds(nodes: TreeNode[]): Set<number> {
  const ids = new Set<number>();
  const walk = (ns: TreeNode[]) => {
    ns.forEach((n) => {
      ids.add(n.id);
      walk(n._children);
    });
  };
  walk(nodes);
  return ids;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const KnowledgeTree: React.FC<KnowledgeTreeProps> = ({
  assets,
  selectedId,
  newlyRevealedId,
  onSelect,
  compact = false,
}) => {
  const [treeSearch, setTreeSearch] = useState("");
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const tree = buildTree(assets);
  const visibleTree = treeSearch.trim() ? filterTree(tree, treeSearch) : tree;

  const toggleNode = (id: number) => {
    setExpandedNodes((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const collapseAll = () => setExpandedNodes(new Set());
  const expandAll = () => {
    // collectAllIds expects AssetTableItem[] — we can just collect from treeNodesToIds
    setExpandedNodes(treeNodesToIds(tree));
  };

  // ── Node renderer ─────────────────────────────────────────────────────────

  const renderNode = (node: TreeNode, depth = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedId === node.id;
    const hasChildren = node._children.length > 0;
    const cfg = getTypeConfig(node.asset.type);
    const dotColor = cfg.color.replace("text-", "bg-");
    const isNew = node.id === newlyRevealedId;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center gap-2 py-1.5 cursor-pointer rounded-sm transition-colors
            ${isSelected ? "bg-amber-400/15 border-l-2 border-amber-400" : "border-l-2 border-transparent"}
            ${!isSelected ? "hover:bg-amber-400/8" : ""}
            ${isNew ? "ring-1 ring-amber-400 animate-pulse" : ""}
          `}
          style={{ paddingLeft: `${8 + depth * 16}px`, paddingRight: "8px" }}
          onClick={() => {
            onSelect(node.id);
            if (hasChildren) toggleNode(node.id);
          }}
        >
          {hasChildren ? (
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="text-zinc-500 text-xs w-3 shrink-0"
            />
          ) : (
            <span className="w-3 flex justify-center shrink-0">
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor} inline-block`} />
            </span>
          )}
          <FontAwesomeIcon
            icon={cfg.icon}
            className={`${cfg.color} text-xs w-4 text-center shrink-0`}
          />
          <span className={`${compact ? "text-xs" : "text-sm"} truncate flex-1 min-w-0`}>
            {node.asset.name}
          </span>
          {isNew && (
            <span className="text-[9px] bg-amber-400/20 text-amber-400 rounded px-1 flex-shrink-0">
              New
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>{node._children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search + controls */}
      <div
        className={`${compact ? "px-2 py-1.5" : "px-3 py-2.5"} border-b border-zinc-800/60 flex-shrink-0`}
      >
        <div className="relative flex items-center gap-1">
          <div className="relative flex-1">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs pointer-events-none"
            />
            <input
              type="search"
              placeholder={compact ? "Search…" : "Search by name, type, category, tags…"}
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 pl-7 pr-2 py-1.5 text-xs outline-none focus:border-amber-400/40 transition-colors rounded-sm"
              value={treeSearch}
              onChange={(e) => setTreeSearch(e.target.value)}
            />
          </div>
          {!compact && (
            <>
              <button
                title="Collapse all"
                className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-xs rounded-sm"
                onClick={collapseAll}
              >
                <FontAwesomeIcon icon={faCompressAlt} />
              </button>
              <button
                title="Expand all"
                className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-xs rounded-sm"
                onClick={expandAll}
              >
                <FontAwesomeIcon icon={faExpandAlt} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tree list */}
      <div className="flex-1 overflow-y-auto p-2 codex-scroll">
        {visibleTree.length === 0 ? (
          <div className="text-zinc-600 text-xs text-center py-8 px-4 leading-relaxed">
            {treeSearch
              ? "No assets match your search."
              : "Your character doesn't know any assets in this campaign yet.\nThe Game Master will share information with you as the story unfolds."}
          </div>
        ) : (
          visibleTree.map((node) => renderNode(node))
        )}
      </div>

      {/* Footer */}
      {!compact && (
        <div className="border-t border-zinc-800 px-3 py-1.5 flex-shrink-0">
          <span className="text-[10px] text-zinc-600">
            {assets.length} asset{assets.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default KnowledgeTree;
